import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, inject, provideBrowserGlobalErrorListeners } from '@angular/core';
import { provideRouter, withNavigationErrorHandler, withViewTransitions } from '@angular/router';

import { authHttpInterceptorFn, provideAuth0 } from '@auth0/auth0-angular';

import { adminTenantOverrideInterceptor } from '@core/interceptors/admin-tenant-override.interceptor';
import { pageForbiddenInterceptor } from '@core/interceptors/page-forbidden.interceptor';
import { sessionRevokedInterceptor } from '@core/interceptors/session-revoked.interceptor';
import { MOCK_INTERCEPTORS, MOCK_PROVIDERS } from '@core/mock/mock-providers';
import { ToastService } from '@core/services/toast.service';

import { routes } from './app.routes';
import { environment } from '../environments/environment';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    // Cross-fades the routed content between pages via the browser's native View Transition
    // API (see styles.scss for the timing) — degrades to an instant swap, no error, on
    // browsers without support (Router feature-detects document.startViewTransition itself).
    // A page that fails to load (lazy chunk gone after a deploy, a guard that throws) otherwise
    // leaves the click without any visible result.
    provideRouter(
      routes,
      withViewTransitions(),
      withNavigationErrorHandler(() => {
        inject(ToastService).error('Die Seite konnte nicht geladen werden. Bitte Seite neu laden.', {
          label: 'Neu laden',
          run: () => window.location.reload(),
        });
      }),
    ),
    // Mock mode (core/mock): the token interceptor needs the Auth0 client, so it is left out, and the mock API goes last.
    provideHttpClient(withInterceptors([
      sessionRevokedInterceptor,
      pageForbiddenInterceptor,
      ...(MOCK_PROVIDERS.length ? [] : [authHttpInterceptorFn]),
      adminTenantOverrideInterceptor,
      ...MOCK_INTERCEPTORS,
    ])),
    ...(MOCK_PROVIDERS.length ? MOCK_PROVIDERS : [provideAuth0({
      domain: environment.auth0Domain,
      clientId: environment.auth0ClientId,
      authorizationParams: {
        redirect_uri: `${window.location.origin}/auth/callback`,
        audience: environment.auth0Audience,
      },
      // A failed token exchange (e.g. denied consent, expired code) lands here instead of
      // Auth0-Angular's default '/' — /auth/callback would otherwise re-process a dead code.
      errorPath: '/access-denied?reason=login-failed',
      // Plain browser cache is fine for a desktop admin portal — no Auth0SecureCache
      // (that's Mobile-Client's Keychain/sessionStorage wrapper for the Capacitor app).
      // Explicit 'localstorage' (default is 'memory'): without it, a full page reload wipes
      // the cached tokens, and useRefreshTokens alone doesn't survive a reload either (the
      // rotating refresh token itself only lived in that same in-memory cache) — the user
      // would be silently logged out on every refresh instead of staying signed in.
      cacheLocation: 'localstorage',
      useRefreshTokens: true,
      useRefreshTokensFallback: true,
      httpInterceptor: {
        allowedList: [
          {
            // Attach a Bearer token to every /api/* call.
            uriMatcher: (uri: string) => uri.startsWith(environment.apiBaseUrl),
            tokenOptions: {
              authorizationParams: { audience: environment.auth0Audience },
            },
          },
        ],
      },
    })]),
  ],
};
